<script lang="ts">
  import { app } from './store.svelte';
  import type { SculptBrushMode } from './store.svelte';
  import NumberSlider from './NumberSlider.svelte';
  import Icon from './lib/Icon.svelte';

  interface BrushDef { id: SculptBrushMode; label: string; icon: string; hint: string; }
  const BRUSHES: BrushDef[] = [
    { id: 'pull',    label: 'Çek',     icon: 'arrow-down-to-line', hint: 'İçeri çek (kazı)' },
    { id: 'push',    label: 'İt',      icon: 'arrow-up-from-line', hint: 'Dışarı it (kabart)' },
    { id: 'inflate', label: 'Şişir',   icon: 'maximize-2',         hint: 'Hacmi büyüt — vertex normaline göre' },
    { id: 'pinch',   label: 'Sıkıştır',icon: 'minimize-2',         hint: 'Bir noktaya topla' },
    { id: 'smooth',  label: 'Yumuşat', icon: 'droplets',           hint: 'Komşu vertex\'leri averajla' }
  ];

  function setBrush<K extends keyof typeof app.brush>(key: K, value: typeof app.brush[K]) {
    app.brush = { ...app.brush, [key]: value };
  }

  function dispatchSculptEvent(name: string) {
    window.dispatchEvent(new CustomEvent(name));
  }
</script>

<section class="sp">
  <div class="sp-head">
    <h3><Icon name="brush" size={11} /> Şekillendir</h3>
    <span class="badge">
      <Icon name="sparkles" size={9} />
      <span>Beta</span>
    </span>
  </div>

  <p class="muted">3D modele tıklayıp sürükleyerek şekil verin. Kil hamuru gibi çalışır — basıp çekin.</p>

  <div class="brushes">
    {#each BRUSHES as b (b.id)}
      <button class="brush" class:active={app.brush.mode === b.id}
        title={b.hint}
        onclick={() => setBrush('mode', b.id)}>
        <span class="brush-icon"><Icon name={b.icon} size={16} /></span>
        <span class="b-label">{b.label}</span>
      </button>
    {/each}
  </div>

  <div class="knobs">
    <NumberSlider label="Yarıçap" unit="mm" min={1} max={60} step={0.5}
      value={app.brush.radius} onInput={(v) => setBrush('radius', v)} />
    <NumberSlider label="Güç" unit="mm" min={0.05} max={5} step={0.05}
      value={app.brush.strength} onInput={(v) => setBrush('strength', v)} />
    <NumberSlider label="Kenar Yumuşaklığı" min={0} max={1} step={0.05}
      value={app.brush.falloff} onInput={(v) => setBrush('falloff', v)} />
    <label class="sym-row" title="Strok'u X=0 düzleminde aynalar — vazo gibi simetrik modeller için">
      <input type="checkbox" checked={app.brush.symmetryX}
        onchange={(e) => setBrush('symmetryX', (e.currentTarget as HTMLInputElement).checked)} />
      <Icon name="flip" size={13} />
      <span>X-ekseni Simetri</span>
    </label>
    <label class="stamp-row">
      <span>Stamp Şekli</span>
      <select value={app.brush.stamp}
        onchange={(e) => setBrush('stamp', (e.currentTarget as HTMLSelectElement).value as typeof app.brush.stamp)}>
        <option value="none">Düz Disk</option>
        <option value="star5">★ 5 Köşeli Yıldız</option>
        <option value="star8">✦ 8 Köşeli Yıldız</option>
        <option value="gear">⚙ Dişli</option>
        <option value="ring">◯ Halka</option>
      </select>
    </label>
  </div>

  <div class="actions">
    <button class="ghost" onclick={() => dispatchSculptEvent('clayforge:sculpt-undo')}>
      <Icon name="undo" size={13} /> Geri Al
    </button>
    <button class="ghost" onclick={() => dispatchSculptEvent('clayforge:sculpt-redo')}>
      <Icon name="redo" size={13} /> İleri Al
    </button>
    <button class="danger" onclick={() => dispatchSculptEvent('clayforge:sculpt-reset')}>
      <Icon name="rotate-ccw" size={13} /> Sıfırla
    </button>
  </div>

  <details class="hint-box">
    <summary><Icon name="info" size={11} /> Klavye kısayolları</summary>
    <ul>
      <li><kbd>1</kbd>–<kbd>5</kbd> — fırça seç (Çek/İt/Şişir/Sıkıştır/Yumuşat)</li>
      <li><kbd>[</kbd> / <kbd>]</kbd> — yarıçap küçült/büyüt</li>
      <li><kbd>Shift</kbd> + drag — Yumuşat (geçici)</li>
      <li><kbd>Ctrl</kbd> + drag — ters yön (Çek↔İt)</li>
      <li><kbd>Ctrl</kbd>+<kbd>Z</kbd> / <kbd>Y</kbd> — geri al / ileri al</li>
    </ul>
  </details>
</section>

<style>
  .sp { display: flex; flex-direction: column; gap: 10px; }
  .sp-head { display: flex; align-items: center; justify-content: space-between; }
  h3 {
    margin: 0; font-size: 10px; color: var(--text-3);
    text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .badge {
    display: inline-flex; align-items: center; gap: 4px;
    background: linear-gradient(135deg, #d04a3a, #c87b3a);
    color: white; font-size: 9px; padding: 3px 8px; border-radius: 5px;
    font-weight: 700; letter-spacing: 0.05em;
    box-shadow: 0 2px 8px -2px rgba(208,74,58,0.5);
  }
  .muted { color: var(--text-muted); font-size: 11px; line-height: 1.5; margin: 0; }

  .brushes {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px;
    padding: 6px;
    background: linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.01));
    border: 1px solid var(--border-soft);
    border-radius: 10px;
  }
  .brush {
    position: relative;
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    padding: 9px 4px;
    background: transparent;
    border: 1px solid transparent; border-radius: 8px;
    cursor: pointer; color: var(--text-3); font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;
  }
  .brush:hover { background: rgba(255,255,255,0.05); color: var(--text-bright); }
  .brush:active { transform: scale(0.96); }
  .brush.active {
    background: linear-gradient(180deg, rgba(26,122,201,0.32), rgba(26,122,201,0.18));
    color: #d6ecff;
    border-color: rgba(74,184,255,0.45);
    box-shadow: 0 0 0 1px rgba(74,184,255,0.18) inset, 0 4px 14px -4px rgba(74,184,255,0.5);
  }
  .brush-icon {
    width: 28px; height: 28px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 7px;
    background: rgba(255,255,255,0.04);
  }
  .brush.active .brush-icon { background: rgba(74,184,255,0.18); }
  .b-label { font-size: 10px; font-weight: 500; }

  .knobs {
    display: flex; flex-direction: column; gap: 8px;
    padding: 10px;
    background: linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.01));
    border: 1px solid var(--border-soft);
    border-radius: 10px;
  }
  .sym-row {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 11px; color: var(--text-2); cursor: pointer; user-select: none;
    padding: 6px 8px; border-radius: 7px;
    transition: background 0.12s;
  }
  .sym-row:hover { background: rgba(255,255,255,0.04); }
  .sym-row input[type="checkbox"] { accent-color: var(--accent, #1a7ac9); margin: 0; }
  .stamp-row {
    display: flex; align-items: center; gap: 8px;
    font-size: 11px; color: var(--text-2); padding: 4px 8px;
  }
  .stamp-row select {
    margin-left: auto; flex: 1;
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft); border-radius: 7px;
    padding: 5px 8px; font-family: inherit; font-size: 11px;
    transition: border-color 0.12s;
  }
  .stamp-row select:hover { border-color: rgba(74,184,255,0.3); }
  .stamp-row select:focus { outline: none; border-color: var(--accent); }

  .actions { display: flex; gap: 5px; }
  .actions button {
    flex: 1; padding: 7px 8px; border-radius: 8px; cursor: pointer;
    font-size: 11px; font-family: inherit; font-weight: 600;
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    transition: background 0.12s, color 0.12s, transform 0.1s, border-color 0.12s;
  }
  .actions button:active { transform: scale(0.96); }
  .actions .ghost {
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft);
  }
  .actions .ghost:hover {
    background: rgba(255,255,255,0.06); color: var(--text-bright);
    border-color: rgba(255,255,255,0.12);
  }
  .actions .danger {
    background: rgba(208, 74, 58, 0.15); color: #ffaaa0;
    border: 1px solid rgba(208, 74, 58, 0.35);
  }
  .actions .danger:hover {
    background: rgba(208, 74, 58, 0.28);
    box-shadow: 0 4px 14px -4px rgba(208,74,58,0.45);
  }

  .hint-box {
    background: var(--bg-viewer-from);
    border: 1px solid var(--border-soft);
    border-radius: 8px; padding: 8px 10px; margin-top: 4px;
  }
  .hint-box summary {
    cursor: pointer; font-size: 11px; color: var(--text-3);
    list-style: none; padding: 2px 0;
    display: inline-flex; align-items: center; gap: 5px;
    font-weight: 500;
  }
  .hint-box summary::-webkit-details-marker { display: none; }
  .hint-box summary:hover { color: var(--text-bright); }
  .hint-box ul { margin: 8px 0 0; padding-left: 16px; font-size: 10.5px; color: var(--text-muted); line-height: 1.7; }
  .hint-box kbd {
    display: inline-block; padding: 2px 6px;
    background: var(--chip-bg);
    border: 1px solid var(--border-soft);
    border-bottom-width: 2px;
    border-radius: 4px;
    font-family: ui-monospace, monospace; font-size: 9.5px; color: var(--text-bright);
  }
</style>
